import { useLocation } from 'react-router'
import { useAuth } from '@/auth/AuthContext'

// Prefix-based resolution (v3.1a audit L2-6): exact-match промахивался на
// динамических сегментах вроде `/showcase/posts/:postId` и `/polls/:pollId`
// → breadcrumb рендерил пустой <b/>. Longest-prefix первым — с учётом порядка
// объявления keys ниже; `/showcase/posts` длиннее `/showcase`, но матчинг
// пробегает Object.entries по порядку — сначала поиск точного совпадения,
// потом длиннейший префикс. Практически: любой роут под `/polls*` попадает в
// «Опросы»; конкретные страницы `/polls/:id` тоже.
const SECTION_LABELS: Record<string, string> = {
  '/dashboard': 'Дашборд',
  '/analytics': 'Аналитика',
  '/billing': 'Биллинг',
  '/showcase': 'Витрина',
  '/polls': 'Опросы',
}

function resolveSectionLabel(pathname: string): string {
  // Сначала exact — быстрый путь для верхнеуровневых страниц.
  if (SECTION_LABELS[pathname]) return SECTION_LABELS[pathname]
  // Иначе longest-prefix среди тех что кончаются на границе сегмента (/ или конец).
  let best = ''
  let bestLen = 0
  for (const prefix in SECTION_LABELS) {
    if (pathname === prefix || pathname.startsWith(prefix + '/')) {
      if (prefix.length > bestLen) {
        best = SECTION_LABELS[prefix]
        bestLen = prefix.length
      }
    }
  }
  return best
}

export function Topbar() {
  const { user } = useAuth()
  const location = useLocation()
  const sectionLabel = resolveSectionLabel(location.pathname)

  return (
    <header className="topbar">
      <div className="breadcrumb">
        <span>TeleGraphyx</span>
        <span className="breadcrumb-sep">/</span>
        <b>{sectionLabel}</b>
      </div>
      <div className="topbar-spacer" />

      <a
        className="topbar-support"
        href="https://max.ru/u/f9LHodD0cOJxyFsfUc55-mWzIyVtOnWwMlagPfClMqClINEKzphlsr_6FQY"
        target="_blank"
        rel="noopener noreferrer"
        title="Связаться с поддержкой в MAX"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        </svg>
        <span>Поддержка</span>
        <span className="topbar-support-meta">пн–пт 09–21 МСК</span>
      </a>

      <div className="topbar-user">
        <div className="user-avatar">{user?.maxAdminID.slice(0, 2) || '?'}</div>
        <div className="user-name">ID {user?.maxAdminID}</div>
      </div>

      <style>{`
        .topbar {
          height: var(--tg-topbar-h, 60px);
          background: rgba(255, 255, 255, 0.92);
          border-bottom: 1px solid var(--tg-line-2, #f0f0f4);
          padding: 0 var(--tg-content-pad, 32px);
          display: flex;
          align-items: center;
          gap: 16px;
          position: sticky;
          top: 0;
          z-index: 10;
          backdrop-filter: blur(8px);
        }
        .breadcrumb {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: var(--tg-mute, #8f9eb0);
          font-weight: 500;
        }
        .breadcrumb b {
          color: var(--tg-ink, #111);
          font-weight: 600;
        }
        .breadcrumb-sep {
          color: var(--tg-mute-2, #c4c4c8);
        }
        .topbar-spacer {
          flex: 1;
        }
        .topbar-support {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 7px 12px 7px 10px;
          border-radius: var(--tg-r-md, 10px);
          background: var(--tg-surface-2, #f5f5f9);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          font-size: 13px;
          font-weight: 600;
          color: var(--tg-ink-2, #333);
          text-decoration: none;
          transition: all 150ms;
        }
        .topbar-support:hover {
          background: var(--tg-blue-bg, #ebf2ff);
          border-color: var(--tg-blue-soft, rgba(0,128,255,0.4));
          color: var(--tg-blue-press, #0066cc);
        }
        .topbar-support svg {
          width: 14px;
          height: 14px;
        }
        .topbar-support-meta {
          color: var(--tg-mute, #8f9eb0);
          font-weight: 500;
          font-size: 12px;
          margin-left: 4px;
        }
        @media (max-width: 900px) {
          .topbar { padding-left: 64px; }
          .topbar-support-meta { display: none; }
        }
        @media (max-width: 640px) {
          .topbar { padding: 0 14px 0 64px; gap: 10px; }
          /* Оставляем только section-name (без «TeleGraphyx /») */
          .breadcrumb span:first-child, .breadcrumb-sep { display: none; }
          /* Только иконка «Поддержка» — текст скрываем */
          .topbar-support span:not(.topbar-support-meta) { display: none; }
          .topbar-support { padding: 7px 10px; }
          /* Только аватар, ID скрываем */
          .user-name { display: none; }
          .topbar-user { padding: 4px; }
        }
        .topbar-search {
          display: flex;
          align-items: center;
          gap: 8px;
          background: var(--tg-surface-2, #f5f5f9);
          padding: 7px 12px;
          border-radius: var(--tg-r-md, 10px);
          width: 280px;
          font-size: 13px;
          color: var(--tg-mute, #8f9eb0);
          cursor: pointer;
          transition: background 150ms;
        }
        .topbar-search:hover {
          background: var(--tg-blue-bg, #ebf2ff);
        }
        .topbar-search svg {
          width: 14px;
          height: 14px;
          opacity: 0.6;
        }
        .topbar-search kbd {
          margin-left: auto;
          font-family: 'JetBrains Mono', monospace;
          font-size: 10px;
          background: white;
          padding: 1px 6px;
          border-radius: 4px;
          color: var(--tg-mute, #8f9eb0);
          border: 1px solid var(--tg-line-2, #f0f0f4);
        }
        .topbar-icon {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          cursor: pointer;
          color: var(--tg-mute, #8f9eb0);
          transition: background 150ms, color 150ms;
          position: relative;
        }
        .topbar-icon:hover {
          background: var(--tg-surface-2, #f5f5f9);
          color: var(--tg-ink, #111);
        }
        .topbar-icon svg {
          width: 18px;
          height: 18px;
        }
        .topbar-icon-dot {
          position: absolute;
          top: 7px;
          right: 7px;
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #d33;
          border: 2px solid white;
        }
        .topbar-user {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 4px 10px 4px 4px;
          border-radius: 999px;
          cursor: pointer;
          transition: background 150ms;
        }
        .topbar-user:hover {
          background: var(--tg-surface-2, #f5f5f9);
        }
        .user-avatar {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: linear-gradient(135deg, #5E72E4, #825EE4);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
          font-size: 11px;
        }
        .user-name {
          font-size: 13px;
          font-weight: 600;
          color: var(--tg-ink, #111);
        }
      `}</style>
    </header>
  )
}
